import { LitElement, html, css, nothing } from 'lit';
import { createRef, ref } from 'lit/directives/ref.js';

import { KatapultDialog } from '../katapult-elements/katapult-dialog.js';
import '../user-chip/user-chip.js';
import { SetUserCompanyAdmin, SetUserReadPermission, SetUserWritePermission } from '../../modules/UserPermissions.js';
import { ListenToRolesGrantedToUserByCompany, SetUserRole } from '../../modules/Roles.js';

/** @import { Ref } from 'lit/directives/ref.js' */

export class ExternalUserDialog extends LitElement {
  static properties = {
    externalUser: { type: Object, state: true },
    externalUserRoles: { type: Object, state: true }
  };

  static styles = css`
    .optionRow {
      display: flex;
      align-items: center;
      padding: var(--sl-spacing-small);
    }
    .optionRow :first-child {
      flex-grow: 1;
    }
    paper-toggle-button {
      --primary-color: var(--secondary-color);
    }
    .label {
      display: flex;
      align-items: center;
      gap: var(--sl-spacing-x-small);
      margin-top: var(--sl-spacing-large);
      opacity: 0.7;
      font-weight: var(--sl-font-weight-semibold);
    }
  `;

  render() {
    const { externalUser, externalUserRoles, serverHasPortal } = this;

    return html`
      <katapult-dialog
        ${ref(this.dialogRef)}
        title="External User"
        icon="background_replace"
        color="var(--paper-grey-300)"
        opened
        closeButton
      >
        <div>
          <user-chip email="${externalUser?.email}" full-email></user-chip>
          <div class="label">
            <katapult-icon icon="security"></katapult-icon>
            <span>Permissions</span>
          </div>
          <div class="optionRow">
            <div>Read Permission</div>
            <paper-toggle-button .checked=${externalUser?.read_permission} @click=${this.toggleReadPermission}></paper-toggle-button>
          </div>
          <div class="optionRow">
            <div>Write Permission</div>
            <paper-toggle-button .checked=${externalUser?.write_permission} @click=${this.toggleWritePermission}></paper-toggle-button>
          </div>
          <div class="optionRow">
            <div>Company Admin</div>
            <paper-toggle-button .checked=${externalUser?.company_admin} @click=${this.toggleCompanyAdmin}></paper-toggle-button>
          </div>
          ${serverHasPortal
            ? html`
                <div class="label">
                  <katapult-icon icon="group"></katapult-icon>
                  <span>Roles</span>
                </div>
                <div class="optionRow">
                  <div>Application Manager</div>
                  <paper-toggle-button
                    .checked=${externalUserRoles?.application_manager}
                    @click=${this.toggleApplicationManager}
                  ></paper-toggle-button>
                </div>
              `
            : nothing}
        </div>
      </katapult-dialog>
    `;
  }

  get dialog() {
    return this.dialogRef.value;
  }

  constructor(companyId, uid, options) {
    super();

    this.externalUser = null;
    this.externalUserRoles = null;

    if (!companyId || !uid) throw new Error('companyId and uid are required');
    this.companyId = companyId;
    this.uid = uid;

    const { serverHasPortal = false } = options ?? {};
    this.serverHasPortal = serverHasPortal === true;

    /**
     * The dialog for editing attachment records
     * @type {Ref<KatapultDialog>}
     */
    this.dialogRef = createRef();

    this.externalUserRef = globalThis.firebase.database().ref(`photoheight/company_space/${this.companyId}/external_users/${this.uid}`);
    this.externalUserCallback = (snapshot) => {
      if (!snapshot.exists()) {
        this.dialog?.close();
        return;
      }
      this.externalUser = snapshot.val();
    };
    this.externalUserRef.on('value', this.externalUserCallback);

    const rolesCallback = (roles) => (this.externalUserRoles = roles);
    this.disconnectExternalUserRolesListener = ListenToRolesGrantedToUserByCompany(this.uid, this.companyId, rolesCallback);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.externalUserRef.off('value', this.externalUserCallback);
    this.disconnectExternalUserRolesListener();
  }

  async toggleReadPermission(e) {
    const input = e.currentTarget;
    input.checked = !input.checked;
    input.disabled = true;
    const newValue = !this.externalUser.read_permission;
    try {
      await SetUserReadPermission(this.uid, this.companyId, newValue);
    } finally {
      input.disabled = false;
    }
  }

  async toggleWritePermission(e) {
    const input = e.currentTarget;
    input.checked = !input.checked;
    input.disabled = true;
    const newValue = !this.externalUser.write_permission;
    try {
      await SetUserWritePermission(this.uid, this.companyId, newValue);
    } finally {
      input.disabled = false;
    }
  }

  async toggleCompanyAdmin(e) {
    const input = e.currentTarget;
    input.checked = !input.checked;
    input.disabled = true;
    const newValue = !this.externalUser.company_admin;
    try {
      await SetUserCompanyAdmin(this.uid, this.companyId, newValue);
    } finally {
      input.disabled = false;
    }
  }

  async toggleApplicationManager(e) {
    const input = e.currentTarget;
    input.checked = !input.checked;
    input.disabled = true;
    const newValue = !this.externalUserRoles?.application_manager;
    try {
      await SetUserRole(this.uid, 'application_manager', this.companyId, newValue);
    } finally {
      input.disabled = false;
    }
  }
}
customElements.define('external-user-dialog', ExternalUserDialog);
